/**
 * EVERY LEAD THE ROOM HAS, to pick one up again: each with what it looks for
 * and the leads inside it, the one being followed marked. Dropping a lead
 * takes it (and the ones inside it) off this list; nothing found while
 * following it goes anywhere.
 */
import type { BoardroomLead } from '../../../types';

/** Pure: the leads as a tree, outermost first, each followed by the leads
 *  inside it. A lead whose parent is gone sits at the top. */
export function leadTree(leads: BoardroomLead[]): { lead: BoardroomLead; depth: number }[] {
  const ids = new Set(leads.map(l => l.id));
  const kids = new Map<number | null, BoardroomLead[]>();
  for (const l of leads) {
    const p = l.parent_id != null && ids.has(l.parent_id) ? l.parent_id : null;
    kids.set(p, [...(kids.get(p) ?? []), l]);
  }
  const out: { lead: BoardroomLead; depth: number }[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const l of kids.get(parent) ?? []) {
      out.push({ lead: l, depth });
      walk(l.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export default function LeadsPanel({
  leads,
  current,
  busy,
  onFollow,
  onDrop,
}: {
  leads: BoardroomLead[];
  current: number | null;
  busy: boolean;
  onFollow: (leadId: number | null) => void;
  onDrop: (lead: BoardroomLead) => void;
}) {
  const rows = leadTree(leads);
  return (
    <div className="space-y-3 p-3 text-sm" data-testid="leads-panel">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        A lead is part of the room you chose to follow. Narrow the tables with tags, then press{' '}
        <span className="font-medium">Follow this lead</span> on the lens bar.
      </p>
      <button
        type="button"
        disabled={busy || current === null}
        onClick={() => onFollow(null)}
        className={`w-full rounded-md border px-2.5 py-2 text-left ${
          current === null
            ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20'
            : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
        }`}
        data-testid="leads-whole-room"
      >
        <span className="font-medium text-gray-900 dark:text-gray-100">The whole room</span>
        {current === null && <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-300">following</span>}
      </button>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No leads yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map(({ lead, depth }) => {
            const on = lead.id === current;
            return (
              <li
                key={lead.id}
                style={{ marginLeft: `${depth * 1}rem` }}
                className={`rounded-md border px-2.5 py-2 ${
                  on
                    ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
                data-testid={`lead-row-${lead.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{lead.title}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">{lead.text}</div>
                    {lead.label && <div className="mt-0.5 text-[11px] text-gray-400">{lead.label}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {on ? (
                      <span className="text-xs text-indigo-600 dark:text-indigo-300">following</span>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onFollow(lead.id)}
                        className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        data-testid={`lead-follow-${lead.id}`}
                      >
                        Follow
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onDrop(lead)}
                      className="rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      data-testid={`lead-drop-${lead.id}`}
                    >
                      Drop
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
